import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { concat, forkJoin, Observable, of } from "rxjs";
import { catchError, last, map, switchMap, tap } from "rxjs/operators";
import { PictureUploadState } from "src/app/annotation/canvas/canvas.component";
import { ChangeInsideSurveyInsideUnitsDTO } from "src/app/shared/Models/write/ChangeInsideSurveyInsideUnitsDto";
import { ChangeInsideSurveyPhotoIndexDTO } from "src/app/shared/Models/write/ChangeInsideSurveyPhotoIndexDto";
import { ChangeInsideSurveyPhotoRemarksDto } from "src/app/shared/Models/write/ChangeInsideSurveyPhotoRemarksDto";
import { ChangeOutsideSurveyPhotoIndexDTO } from "src/app/shared/Models/write/ChangeOutsideSurveyPhotoIndexDto";
import { ChangeOutsideSurveyPhotoRemarksDto } from "src/app/shared/Models/write/ChangeOutsideSurveyPhotoRemarksDto";
import { ChangeOutsideUnitInfoFromInsideSurveyDTO } from "src/app/shared/Models/write/ChangeOutsideUnitInfoFromInsideSurveyDTO";
import { LayoutDto } from "src/app/shared/Models/write/LayoutDto";
import { SaveOutsideSurveyUnitInfoDto } from "src/app/shared/Models/write/SaveOutsideSurveyUnitInfoDto";
import { SaveOutsideSurveyMainAddressDto } from "src/app/shared/Models/write/SaveOutsideSurveyMainAddressDto";
import { ChangeEngineerDto } from "src/app/shared/Models/write/ChangeEngineerDto";
import { UpdateInsideSurveyTechnicalInfoDTO } from "src/app/shared/Models/write/updateInsideSurveyTechnicalInfoDTO";
import {
  SaveValidateInsideSurveyDto,
  SaveValidateOutsideSurveyDto,
} from "src/app/shared/Models/write/ValidateSurveyDto";
import { InsideSurveyService } from "src/app/shared/services/inside-survey.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { OutsideSurveyService } from "src/app/shared/services/outside-survey.service";
import { PhotoQueueService } from "./photo-queue.service";
import { PhotoService, SurveyType } from "./photo.service";

interface PhotoToRemoveState {
  id: string;
  surveyType: SurveyType;
}

interface switchOnAddressOutsideSurveyPhotoRemarksDto {
  dto: ChangeOutsideSurveyPhotoRemarksDto;
  addressId: string;
}

interface State {
  photosToRemove?: PhotoToRemoveState[];
  photoReorder?: (
    | ChangeInsideSurveyPhotoIndexDTO
    | ChangeOutsideSurveyPhotoIndexDTO
  )[];
  insideCablingLayout?: LayoutDto;
  insideOutsideSurveyTechnicalInfo?: any[];
  insideSurveyOutsidePhotoForm?: switchOnAddressOutsideSurveyPhotoRemarksDto[];
  insideSurveyTechnicalInfo?: UpdateInsideSurveyTechnicalInfoDTO;
  insideSurveyOutsideUnits?: ChangeOutsideUnitInfoFromInsideSurveyDTO[];
  insideSurveyInsideUnits?: ChangeInsideSurveyInsideUnitsDTO;
  insideSurveyPhotoTab?: ChangeInsideSurveyPhotoRemarksDto;
  insideSurveyMainAddress?: SaveOutsideSurveyMainAddressDto[];
  outsideSurveyMainPhoto?: ChangeOutsideSurveyPhotoRemarksDto[];
  outsideSurveyUnit?: SaveOutsideSurveyUnitInfoDto;
  outsideSurveyMainAddress?: SaveOutsideSurveyMainAddressDto;
  outsideSurveyTechnical?: any;
  changeEngineerDto?: ChangeEngineerDto;
  outsideSurveySaveValidation?: SaveValidateOutsideSurveyDto;
  insideSurveySaveValidation?: SaveValidateInsideSurveyDto;
}

@Injectable({
  providedIn: "root",
})
export class SavingStateService {
  state: State = {
    insideSurveyOutsidePhotoForm: [],
    photosToRemove: [],
    photoReorder: [],
    insideSurveyOutsideUnits: [],
    insideOutsideSurveyTechnicalInfo: [],
    outsideSurveyMainPhoto: [],
    insideSurveyMainAddress: [],
  };

  LOADER_TAG: string = "SAVE_STATE";

  constructor(
    private outsideSurveyService: OutsideSurveyService,
    private insideSurveyService: InsideSurveyService,
    private photoService: PhotoService,
    private loaderService: LoaderService,
    private photoQueueService: PhotoQueueService,
    private readonly router: Router
  ) {}

  canDeactivateCheck() {
    if (
      this.photoQueueService.hasFailed ||
      this.photoQueueService.queueRunning ||
      this.state.photosToRemove.length > 0 ||
      this.state.insideCablingLayout ||
      this.state.insideOutsideSurveyTechnicalInfo.length > 0 ||
      this.state.insideSurveyTechnicalInfo ||
      this.state.insideSurveyOutsidePhotoForm.length > 0 ||
      this.state.insideSurveyOutsideUnits.length > 0 ||
      this.state.insideSurveyInsideUnits ||
      this.state.insideSurveyPhotoTab ||
      this.state.outsideSurveyMainPhoto.length > 0 ||
      this.state.outsideSurveyUnit ||
      this.state.outsideSurveyTechnical ||
      this.state.outsideSurveySaveValidation ||
      this.state.insideSurveySaveValidation ||
      this.state.outsideSurveyMainAddress ||
      this.state.insideSurveyMainAddress.length > 0
    )
      return false;
    return true;
  }

  discardChanges() {
    this.state = {
      insideSurveyOutsidePhotoForm: [],
      photosToRemove: [],
      photoReorder: [],
      insideSurveyOutsideUnits: [],
      insideOutsideSurveyTechnicalInfo: [],
      outsideSurveyMainPhoto: [],
      insideSurveyMainAddress: [],
    };
  }

  executeRemoves(): Observable<boolean> {
    let subscriptions = [];

    if (this.state.photosToRemove?.length > 0)
      subscriptions.push(
        forkJoin(
          this.state.photosToRemove.map((dto) => this.removePhotoOffline(dto))
        ).pipe(tap(() => (this.state.photosToRemove = [])))
      );
    if (subscriptions.length > 0) {
      return concat(...subscriptions).pipe(
        last(),
        switchMap(() => {
          this.loaderService.hide(this.LOADER_TAG);
          return of(true);
        }),

        catchError((err) => {
          this.loaderService.hide(this.LOADER_TAG);
          return of(false);
        })
      );
    } else {
      this.loaderService.hide(this.LOADER_TAG);
      return of(true);
    }
  }

  // shared
  saveAll(
    reroute: boolean,
    callback?: Observable<Object>
  ): Observable<boolean> {
    this.loaderService.show(this.LOADER_TAG);

    let subscriptions = [];

    if (this.photoQueueService.hasFailed || this.photoQueueService.queueRunning)
      subscriptions.push(this.photoQueueService.retryFailed());

    // Inside Survey
    if (this.state.photosToRemove?.length > 0)
      subscriptions.push(
        forkJoin(
          this.state.photosToRemove.map((dto) => this.removePhotoOffline(dto))
        ).pipe(tap(() => (this.state.photosToRemove = [])))
      );

    if (this.state.photoReorder?.length > 0)
      subscriptions.push(
        forkJoin(
          this.state.photoReorder.map((dto) => {
            if (!dto.photosIndexInfo.length) return of(true);
            return this.photoService.changePhotoIndexAsync(dto);
          })
        ).pipe(tap(() => (this.state.photoReorder = [])))
      );

    if (this.state.insideCablingLayout)
      subscriptions.push(
        this.saveInsideSurveyCablingLayout(this.state.insideCablingLayout).pipe(
          tap(() => (this.state.insideCablingLayout = null))
        )
      );

    if (this.state.insideSurveyPhotoTab)
      subscriptions.push(
        this.saveInsideSurveyPhotoTab(this.state.insideSurveyPhotoTab).pipe(
          tap(() => (this.state.insideSurveyPhotoTab = null))
        )
      );

    if (this.state.outsideSurveySaveValidation)
      subscriptions.push(
        this.saveOutsideSurveySaveValidation(
          this.state.outsideSurveySaveValidation
        ).pipe(tap(() => (this.state.outsideSurveySaveValidation = null)))
      );

    if (this.state.insideSurveyMainAddress?.length > 0)
      subscriptions.push(
        forkJoin(
          this.state.insideSurveyMainAddress.map((dto) =>
            this.saveInsideSurveyAddress(dto)
          )
        ).pipe(tap(() => (this.state.insideSurveyMainAddress = [])))
      );

    if (this.state.outsideSurveyMainAddress)
      subscriptions.push(
        this.saveOutsideSurveyAddress(this.state.outsideSurveyMainAddress).pipe(
          tap(() => (this.state.outsideSurveyMainAddress = null))
        )
      );

    if (this.state.insideSurveySaveValidation)
      subscriptions.push(
        this.saveInsideSurveySaveValidation(
          this.state.insideSurveySaveValidation
        ).pipe(tap(() => (this.state.insideSurveySaveValidation = null)))
      );

    if (this.state.insideOutsideSurveyTechnicalInfo?.length > 0)
      subscriptions.push(
        forkJoin(
          this.state.insideOutsideSurveyTechnicalInfo.map((dto) =>
            this.saveInsideOutsideSurveyTechnicalInfo(dto)
          )
        ).pipe(tap(() => (this.state.insideOutsideSurveyTechnicalInfo = [])))
      );

    if (this.state.insideSurveyTechnicalInfo)
      subscriptions.push(
        this.saveInsideSurveyTechnicalInfo(
          this.state.insideSurveyTechnicalInfo
        ).pipe(tap(() => (this.state.insideSurveyTechnicalInfo = null)))
      );

    if (this.state.insideSurveyOutsidePhotoForm?.length > 0)
      subscriptions.push(
        forkJoin(
          this.state.insideSurveyOutsidePhotoForm.map((dto) =>
            this.saveOutsideSurveyPhotoTab(dto.dto)
          )
        ).pipe(tap(() => (this.state.insideSurveyOutsidePhotoForm = [])))
      );

    if (this.state.insideSurveyOutsideUnits?.length > 0)
      subscriptions.push(
        forkJoin(
          this.state.insideSurveyOutsideUnits.map((dto) =>
            this.saveInsideSurveyOutsideUnits(dto)
          )
        ).pipe(tap(() => (this.state.insideSurveyOutsideUnits = [])))
      );

    if (this.state.insideSurveyInsideUnits)
      subscriptions.push(
        this.saveInsideSurveyInsideUnits(
          this.state.insideSurveyInsideUnits
        ).pipe(tap(() => (this.state.insideSurveyInsideUnits = null)))
      );

    // Outside Survey
    if (this.state.outsideSurveyMainPhoto?.length > 0)
      subscriptions.push(
        forkJoin(
          this.state.outsideSurveyMainPhoto.map((dto) =>
            this.saveOutsideSurveyMainPhoto(dto)
          )
        ).pipe(tap(() => (this.state.outsideSurveyMainPhoto = [])))
      );

    if (this.state.outsideSurveyUnit)
      subscriptions.push(
        this.saveOutsideSurveyUnit(this.state.outsideSurveyUnit).pipe(
          tap(() => (this.state.outsideSurveyUnit = null))
        )
      );

    if (this.state.outsideSurveyTechnical)
      subscriptions.push(
        this.saveOutsideSurveyTechnical(this.state.outsideSurveyTechnical).pipe(
          tap(() => (this.state.outsideSurveyTechnical = null))
        )
      );

    if (this.state.changeEngineerDto)
      subscriptions.push(
        this.saveOutsideSurveyEngineer(this.state.changeEngineerDto).pipe(
          tap(() => (this.state.changeEngineerDto = null))
        )
      );

    if (callback) subscriptions.push(callback);

    // Subs check
    if (subscriptions.length > 0) {
      return concat(...subscriptions).pipe(
        last(),
        switchMap(() => {
          this.discardChanges();
          // can only navigate iff all changes are succesfull
          this.loaderService.hide(this.LOADER_TAG);

          if (reroute) this.router.navigate(["/building-table"]);

          return of(true);
        }),

        catchError((err) => {
          this.loaderService.hide(this.LOADER_TAG);
          return of(false);
        })
      );
    } else {
      this.loaderService.hide(this.LOADER_TAG);
      // can only navigate if no changes are required

      if (reroute) this.router.navigate(["/building-table"]);

      return of(true);
    }
  }

  addPhotoToRemove(imgId: string, surveyType: SurveyType) {
    this.state.photosToRemove.push({ id: imgId, surveyType: surveyType });
  }

  removePhotoOffline(imageToRemove: PhotoToRemoveState) {
    return this.photoQueueService.removePhoto(
      imageToRemove.id,
      imageToRemove.surveyType,
      true
    );
  }

  addPhotoReorder(
    dto: ChangeInsideSurveyPhotoIndexDTO | ChangeOutsideSurveyPhotoIndexDTO
  ) {
    var index = this.state.photoReorder.findIndex((d) => d.id == dto.id);
    if (index != -1) this.state.photoReorder[index] = dto;
    else this.state.photoReorder.push(dto);
  }

  removePhotoReorder(id: string) {
    var index = this.state.photoReorder.findIndex((d) => d.id == id);
    if (index != -1) this.state.photoReorder.splice(index, 1);
  }

  // end shared
  /// BEGIN OUTSIDE SURVEY

  // TODO CONTACT INFO TAB OUTSIDE SURVEY (flow is not clear)

  // Begin OutsideSurveyTechnical
  addOutsideSurveyTechnical(dto: any) {
    this.state.outsideSurveyTechnical = dto;
    return of(true);
  }

  saveOutsideSurveyTechnical(dto: any) {
    return this.outsideSurveyService.saveTechnical(dto);
  }
  // End OutsideSurveyTechnical

  // Begin OutsideSurveyTechnical
  updateOutsideSurveyEngineer(dto: ChangeEngineerDto) {
    this.state.changeEngineerDto = dto;
    return of(true);
  }

  saveOutsideSurveyEngineer(dto: ChangeEngineerDto) {
    return this.outsideSurveyService.saveOutsideSurveyEngineer(dto);
  }
  // End OutsideSurveyTechnical

  // Begin InsideSurveyMainAddress
  addInsideSurveyAddress(dto: SaveOutsideSurveyMainAddressDto) {
    let index = this.state.insideSurveyMainAddress.findIndex(
      (e) => e.surveyId == dto.surveyId && e.buildingId == dto.buildingId
    );

    if (index == -1) this.state.insideSurveyMainAddress.push(dto);
    else this.state.insideSurveyMainAddress[index] = dto;
    return of(true);
  }

  saveInsideSurveyAddress(dto: SaveOutsideSurveyMainAddressDto) {
    return this.outsideSurveyService.saveMainAddress(dto);
  }
  // End InsideSurveyMainAddress

  // Begin OutsideSurveyMainAddress
  addOutsideSurveyAddress(dto: SaveOutsideSurveyMainAddressDto) {
    this.state.outsideSurveyMainAddress = dto;
    return of(true);
  }

  saveOutsideSurveyAddress(dto: SaveOutsideSurveyMainAddressDto) {
    return this.outsideSurveyService.saveMainAddress(dto);
  }
  // End OutsideSurveyMainAddress

  // Begin OutsideSurveyUnit
  addOutsideSurveyUnit(dto: SaveOutsideSurveyUnitInfoDto) {
    this.state.outsideSurveyUnit = dto;
    return of(true);
  }

  saveOutsideSurveyUnit(dto: SaveOutsideSurveyUnitInfoDto) {
    return this.outsideSurveyService.saveUnit(dto);
  }
  // End OutsideSurveyUnit

  // Begin OutsideSurveyMainPhoto
  addOutsideSurveyMainPhoto(dto: ChangeOutsideSurveyPhotoRemarksDto) {
    this.state.outsideSurveyMainPhoto.push(dto);
    return of(true);
  }

  saveOutsideSurveyMainPhoto(dto: ChangeOutsideSurveyPhotoRemarksDto) {
    return this.outsideSurveyService.savePhotoTab(dto);
  }
  // End OutsideSurveyMainPhoto

  /// END OUTSIDE SURVEY

  /// BEGIN INSIDE SURVEY
  // TODO CONTACT INFO TAB INSIDE SURVEY (flow is not clear)

  // Begin InsideSurveyLayout
  addInsideSurveyCablingLayout(dto: LayoutDto) {
    this.state.insideCablingLayout = dto;
    return of(true);
  }

  reuploadInsideSurveyCablingLayout() {
    if (!this.state.insideCablingLayout) return of(null);
    return this.insideSurveyService
      .updateLayout(this.state.insideCablingLayout)
      .pipe(
        map(() => {
          this.state.insideCablingLayout = null;
          return PictureUploadState.done;
        }),
        catchError(() => of(PictureUploadState.failed))
      );
  }

  saveInsideSurveyCablingLayout(dto: LayoutDto) {
    return this.insideSurveyService.updateLayout(dto);
  }
  // End InsideSurveyLayout

  // Begin InsideSurveyInsideUnits
  addInsideSurveyInsideUnits(dto: ChangeInsideSurveyInsideUnitsDTO) {
    this.state.insideSurveyInsideUnits = dto;
    return of(true);
  }

  saveInsideSurveyInsideUnits(dto: ChangeInsideSurveyInsideUnitsDTO) {
    return this.insideSurveyService.changeInsideUnitInfoFromInsideSurvey(dto);
  }
  // End InsideSurveyInsideUnits

  // Begin InsideSurveyOutsideUnits
  addInsideSurveyOutsideUnits(dto: ChangeOutsideUnitInfoFromInsideSurveyDTO) {
    let index = this.state.insideSurveyOutsideUnits.findIndex(
      (e) => e.surveyId == dto.surveyId && e.buildingId == dto.buildingId
    );
    if (index == -1) this.state.insideSurveyOutsideUnits.push(dto);
    else this.state.insideSurveyOutsideUnits[index] = dto;
    return of(true);
  }

  saveInsideSurveyOutsideUnits(dto: ChangeOutsideUnitInfoFromInsideSurveyDTO) {
    return this.insideSurveyService.changeOutsideUnitInfoFromInsideSurvey(dto);
  }
  // End InsideSurveyOutsideUnits

  // Begin InsideSurveyPhotoTab
  addInsideSurveyOutsidePhotoForm(
    remarksDto: ChangeOutsideSurveyPhotoRemarksDto,
    addressId: string
  ) {
    let index = this.state.insideSurveyOutsidePhotoForm.findIndex(
      (e) => e.dto.surveyId == remarksDto.surveyId && e.addressId == addressId
    );
    var dto = {
      dto: remarksDto,
      addressId: addressId,
    } as switchOnAddressOutsideSurveyPhotoRemarksDto;
    if (index == -1) {
      this.state.insideSurveyOutsidePhotoForm.push(dto);
    } else {
      dto.dto.facadeDetailPhotoRemarks =
        this.state.insideSurveyOutsidePhotoForm[index].dto
          .facadeDetailPhotoRemarks.length > 0
          ? [
              ...this.state.insideSurveyOutsidePhotoForm[index].dto
                .facadeDetailPhotoRemarks,
              ...remarksDto.facadeDetailPhotoRemarks,
            ]
          : remarksDto.facadeDetailPhotoRemarks;
      this.state.insideSurveyOutsidePhotoForm[index] = dto;
    }
    return of(true);
  }

  saveOutsideSurveyPhotoTab(dto: ChangeOutsideSurveyPhotoRemarksDto) {
    return this.outsideSurveyService.savePhotoTab(dto);
  }
  // End InsideSurveyPhotoTab

  // Begin InsideSurveyTechnicalInfo
  addInsideSurveyTechnicalInfo(dto: UpdateInsideSurveyTechnicalInfoDTO) {
    var proximusEquipmentPhotoRemarks =
      this.state.insideSurveyTechnicalInfo?.proximusEquipmentPhotoRemarks
        .length > 0
        ? [
            ...this.state.insideSurveyTechnicalInfo
              .proximusEquipmentPhotoRemarks,
            ...dto.proximusEquipmentPhotoRemarks,
          ]
        : dto.proximusEquipmentPhotoRemarks;
    var telcoRoomIntroPhotoRemarks =
      this.state.insideSurveyTechnicalInfo?.telcoRoomIntroPhotoRemarks.length >
      0
        ? [
            ...this.state.insideSurveyTechnicalInfo.telcoRoomIntroPhotoRemarks,
            ...dto.telcoRoomIntroPhotoRemarks,
          ]
        : dto.telcoRoomIntroPhotoRemarks;
    var generalTechnicalInfoPhotoRemark =
      this.state.insideSurveyTechnicalInfo?.generalTechnicalInfoPhotoRemark
        .length > 0
        ? [
            ...this.state.insideSurveyTechnicalInfo
              .generalTechnicalInfoPhotoRemark,
            ...dto.generalTechnicalInfoPhotoRemark,
          ]
        : dto.generalTechnicalInfoPhotoRemark;

    var insideCopperIntroPhotoRemarks =
      this.state.insideSurveyTechnicalInfo?.insideCopperIntroPhotoRemarks
        .length > 0
        ? [
            ...this.state.insideSurveyTechnicalInfo
              .insideCopperIntroPhotoRemarks,
            ...dto.insideCopperIntroPhotoRemarks,
          ]
        : dto.insideCopperIntroPhotoRemarks;
    var outsideCopperIntroPhotoRemarks =
      this.state.insideSurveyTechnicalInfo?.outsideCopperIntroPhotoRemarks
        .length > 0
        ? [
            ...this.state.insideSurveyTechnicalInfo
              .outsideCopperIntroPhotoRemarks,
            ...dto.outsideCopperIntroPhotoRemarks,
          ]
        : dto.outsideCopperIntroPhotoRemarks;
    var insideFibreIntroPhotoRemarks =
      this.state.insideSurveyTechnicalInfo?.insideFibreIntroPhotoRemarks
        .length > 0
        ? [
            ...this.state.insideSurveyTechnicalInfo
              .insideFibreIntroPhotoRemarks,
            ...dto.insideFibreIntroPhotoRemarks,
          ]
        : dto.insideFibreIntroPhotoRemarks;
    var outsideFibreIntroPhotoRemarks =
      this.state.insideSurveyTechnicalInfo?.outsideFibreIntroPhotoRemarks
        .length > 0
        ? [
            ...this.state.insideSurveyTechnicalInfo
              .outsideFibreIntroPhotoRemarks,
            ...dto.outsideFibreIntroPhotoRemarks,
          ]
        : dto.outsideFibreIntroPhotoRemarks;

    // single image remarks
    var roomAvailableForNewEquipmentPhotoRemark =
      dto.roomAvailableForNewEquipmentPhotoRemark?.length > 0
        ? dto.roomAvailableForNewEquipmentPhotoRemark
        : this.state.insideSurveyTechnicalInfo
            ?.roomAvailableForNewEquipmentPhotoRemark;
    var electricityInTelcoRoomPhotoRemark =
      dto.electricityInTelcoRoomPhotoRemark?.length > 0
        ? dto.electricityInTelcoRoomPhotoRemark
        : this.state.insideSurveyTechnicalInfo
            ?.electricityInTelcoRoomPhotoRemark;

    var newDto = {
      ...dto,
      proximusEquipmentPhotoRemarks: proximusEquipmentPhotoRemarks,
      telcoRoomIntroPhotoRemarks: telcoRoomIntroPhotoRemarks,
      generalTechnicalInfoPhotoRemark: generalTechnicalInfoPhotoRemark,
      insideCopperIntroPhotoRemarks: insideCopperIntroPhotoRemarks,
      outsideCopperIntroPhotoRemarks: outsideCopperIntroPhotoRemarks,
      insideFibreIntroPhotoRemarks: insideFibreIntroPhotoRemarks,
      outsideFibreIntroPhotoRemarks: outsideFibreIntroPhotoRemarks,
      electricityInTelcoRoomPhotoRemark: electricityInTelcoRoomPhotoRemark,
      roomAvailableForNewEquipmentPhotoRemark:
        roomAvailableForNewEquipmentPhotoRemark,
    } as UpdateInsideSurveyTechnicalInfoDTO;
    this.state.insideSurveyTechnicalInfo = newDto;
    return of(true);
  }

  saveInsideSurveyTechnicalInfo(dto: UpdateInsideSurveyTechnicalInfoDTO) {
    return this.insideSurveyService.updateInsideSurveyTechnicalInfo(dto);
  }
  // End InsideSurveyTechnicalInfo

  // Begin InsideOutsideSurveyTechnicalInfo
  addInsideOutsideSurveyTechnicalInfo(dto: any) {
    let index = this.state.insideOutsideSurveyTechnicalInfo.findIndex(
      (e) => e.id == dto.id
    );
    if (index == -1) this.state.insideOutsideSurveyTechnicalInfo.push(dto);
    else this.state.insideOutsideSurveyTechnicalInfo[index] = dto;
    return of(true);
  }

  saveInsideOutsideSurveyTechnicalInfo(dto: any) {
    return this.outsideSurveyService.saveTechnical(dto);
  }
  // End InsideOutsideSurveyTechnicalInfo

  // Begin InsideSurveyPhotoTab
  addInsideSurveyPhotoTab(dto: ChangeInsideSurveyPhotoRemarksDto) {
    if (this.state.insideSurveyPhotoTab)
      this.state.insideSurveyPhotoTab.generalPhotoRemarks =
        this.state.insideSurveyPhotoTab?.generalPhotoRemarks.length > 0
          ? [
              ...this.state.insideSurveyPhotoTab.generalPhotoRemarks,
              ...dto.generalPhotoRemarks,
            ]
          : dto.generalPhotoRemarks;
    else this.state.insideSurveyPhotoTab = dto;
    return of(true);
  }

  saveInsideSurveyPhotoTab(dto: ChangeInsideSurveyPhotoRemarksDto) {
    return this.insideSurveyService.savePhotoTab(dto);
  }
  // End InsideSurveyPhotoForm
  /// END INSIDE SURVEY

  // behandel Delete niet enkel override
  addOutsideSurveySaveValidation(dto: SaveValidateOutsideSurveyDto) {
    this.state.outsideSurveySaveValidation = dto;
    return of(true);
  }

  saveOutsideSurveySaveValidation(dto: SaveValidateOutsideSurveyDto) {
    return this.outsideSurveyService.saveValidateSurvey(dto);
  }

  addInsideSurveySaveValidation(dto: SaveValidateInsideSurveyDto) {
    this.state.insideSurveySaveValidation = dto;
    return of(true);
  }

  saveInsideSurveySaveValidation(dto: SaveValidateInsideSurveyDto) {
    return this.insideSurveyService.saveValidation(dto);
  }
}
